import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  FileText, Play, Building2, UserPlus, ClipboardEdit, Loader2,
  ArrowRight, CheckCircle2, MoreVertical, Trash2, Pencil, RotateCcw, History,
} from "lucide-react";
import { isSelfAssessmentOnly, isHrvQuestionnaire } from "@/lib/questionnaireType";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getLogoUrl } from "@/utils/getLogoUrl";
import { LogoUpload, uploadLogoForEntity } from "@/components/admin/LogoUpload";
import type { LogoPathArgs } from "@/lib/slugify";
import { AssessmentVersionHistoryModal } from "@/components/tier/AssessmentVersionHistoryModal";

interface CompanyRow {
  portfolio_id: string;
  assessment_id: string | null;
  name: string;
  logo_url: string | null;
  questionnaire_id: string | null;
  rating: string;
  score: number | null;
  respondent_score: number | null;
  reviewer_score: number | null;
  completion_pct: number;
  assessment_status: string;
  hrv_composite_score: number | null;
  hrv_exposure_label: string | null;
  version_number?: number | null;
}

interface Tier1ClientCardsProps {
  companies: CompanyRow[];
  onRefresh?: () => void;
}

type CardState = "no_assessment" | "in_progress" | "completed";

function getCardState(company: CompanyRow): CardState {
  const isSubmitted = ["Submitted", "Reviewed", "Locked", "Closed"].includes(company.assessment_status);
  if (isSubmitted) return "completed";
  if (company.assessment_id && (company.completion_pct > 0 || company.assessment_status === "In Progress")) return "in_progress";
  return "no_assessment";
}

const STATUS_CONFIG: Record<CardState, { label: string; className: string }> = {
  no_assessment: { label: "Not Started", className: "bg-muted text-muted-foreground" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700 border-blue-200" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-200" },
};

const RATING_COLORS: Record<string, string> = {
  Extensive: "text-green-600",
  Strong: "text-blue-600",
  Adequate: "text-yellow-600",
  Minimal: "text-orange-600",
};

export function Tier1ClientCards({ companies, onRefresh }: Tier1ClientCardsProps) {
  const navigate = useNavigate();
  const [showAssessmentMode, setShowAssessmentMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyRow | null>(null);

  // Invite client modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDisplayName, setInviteDisplayName] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviting, setInviting] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState("");
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-run state
  const [rerunning, setRerunning] = useState(false);
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [rerunCompany, setRerunCompany] = useState<CompanyRow | null>(null);

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyCompany, setHistoryCompany] = useState<CompanyRow | null>(null);

  if (companies.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No clients yet. Create your first client to get started.</p>
      </div>
    );
  }

  const handleCardClick = (company: CompanyRow) => {
    navigate(`/org-admin/${company.portfolio_id}/assessments`);
  };

  const handlePrimaryAction = (e: React.MouseEvent, company: CompanyRow) => {
    e.stopPropagation();
    const state = getCardState(company);

    switch (state) {
      case "completed": {
        if (!company.assessment_id) {
          toast.error("No assessment found for this client");
          return;
        }
        const reportPath = isHrvQuestionnaire(company.questionnaire_id)
          ? `/reports/hrv/${company.assessment_id}`
          : `/reports/self/${company.assessment_id}`;
        navigate(reportPath);
        break;
      }
      case "in_progress":
        navigate(`/wizard/${company.assessment_id}`);
        break;
      case "no_assessment":
        setSelectedCompany(company);
        setShowAssessmentMode(true);
        break;
    }
  };

  const handleCompleteMyselfClick = async () => {
    if (!selectedCompany) return;
    if (selectedCompany.assessment_id) {
      navigate(`/wizard/${selectedCompany.assessment_id}`);
    } else {
      navigate(`/org-admin/${selectedCompany.portfolio_id}/assessments`);
    }
    setShowAssessmentMode(false);
  };

  const handleInviteClientClick = () => {
    setShowAssessmentMode(false);
    setShowInviteModal(true);
    setInviteEmail("");
    setInviteDisplayName("");
    setInvitePassword("");
  };

  const handleInviteSubmit = async () => {
    if (!selectedCompany || !inviteEmail.trim()) {
      toast.error("Email is required");
      return;
    }

    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          portfolio_id: selectedCompany.portfolio_id,
          email: inviteEmail.trim().toLowerCase(),
          password: invitePassword || undefined,
          display_name: inviteDisplayName.trim() || null,
          member_role: "respondent",
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      toast.success(`Client respondent "${inviteEmail}" created and assigned`);
      setShowInviteModal(false);
    } catch (e: any) {
      console.error("Invite client error:", e);
      toast.error(e?.message || "Failed to invite client");
    } finally {
      setInviting(false);
    }
  };

  // ── Delete Client ──
  const handleDeleteClick = (e: React.MouseEvent, company: CompanyRow) => {
    e.stopPropagation();
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCompany) return;
    setDeleting(true);
    try {
      const { error } = await (supabase as any).rpc("admin_delete_portfolio_company", {
        p_portfolio_id: selectedCompany.portfolio_id,
        p_reason: "Deleted by Tier 1 PM via dashboard",
      });
      if (error) throw error;
      toast.success(`"${selectedCompany.name}" has been deleted`);
      setShowDeleteModal(false);
      onRefresh?.();
    } catch (e: any) {
      console.error("Delete client error:", e);
      toast.error(e?.message || "Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  // ── Edit Client ──
  const handleEditClick = (e: React.MouseEvent, company: CompanyRow) => {
    e.stopPropagation();
    setSelectedCompany(company);
    setEditName(company.name);
    setEditLogoFile(null);
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!selectedCompany || !editName.trim()) return;
    setSaving(true);
    try {
      // Update name
      const { error } = await supabase
        .from("portfolios")
        .update({ name: editName.trim() })
        .eq("id", selectedCompany.portfolio_id);
      if (error) throw error;

      // Upload new logo if selected
      if (editLogoFile) {
        try {
          const pathArgs: LogoPathArgs = {
            level: "portfolio",
            clientName: "client",
            subOrgName: "sub-org",
            companyName: editName.trim(),
            entityId: selectedCompany.portfolio_id,
          };
          await uploadLogoForEntity(editLogoFile, pathArgs);
        } catch (logoErr) {
          console.error("Logo upload failed:", logoErr);
          toast.error("Name updated but logo upload failed");
        }
      }

      toast.success("Client updated");
      setShowEditModal(false);
      onRefresh?.();
    } catch (e: any) {
      console.error("Edit client error:", e);
      toast.error(e?.message || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  // ── Re-run Assessment (opens confirmation first) ──
  const handleRerunClick = (e: React.MouseEvent, company: CompanyRow) => {
    e.stopPropagation();
    if (!company.questionnaire_id) {
      toast.error("No questionnaire assigned to this client");
      return;
    }
    setRerunCompany(company);
    setShowRerunConfirm(true);
  };

  const handleRerunConfirm = async () => {
    if (!rerunCompany?.questionnaire_id) return;
    setRerunning(true);
    try {
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr || !userData?.user?.id) {
        throw new Error("Not authenticated");
      }
      const { data: newId, error } = await (supabase as any).rpc("ensure_draft_assessment", {
        p_org_id: rerunCompany.portfolio_id,
        p_questionnaire_id: rerunCompany.questionnaire_id,
        p_user_id: userData.user.id,
      });
      if (error) throw error;
      toast.success("New assessment version created");
      setShowRerunConfirm(false);
      navigate(`/wizard/${newId}`);
    } catch (e: any) {
      console.error("Re-run assessment error:", e);
      toast.error(e?.message || "Failed to create new assessment");
    } finally {
      setRerunning(false);
    }
  };

  // ── View History ──
  const handleHistoryClick = (e: React.MouseEvent, company: CompanyRow) => {
    e.stopPropagation();
    setHistoryCompany(company);
    setShowHistoryModal(true);
  };

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {companies.map((company) => {
          const state = getCardState(company);
          const statusConfig = STATUS_CONFIG[state];
          const selfOnly = isSelfAssessmentOnly(company.questionnaire_id);
          const isHrv = isHrvQuestionnaire(company.questionnaire_id);
          const displayScore = isHrv
            ? (company.hrv_composite_score != null
              ? Math.round(((company.hrv_composite_score - 1) / 2) * 100)
              : null)
            : (selfOnly ? company.respondent_score : company.score);
          const ratingColor = RATING_COLORS[company.rating] ?? "text-muted-foreground";

          return (
            <Card
              key={company.portfolio_id}
              className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleCardClick(company)}
            >
              <CardContent className="p-5 space-y-4">
                {/* Top row: status badge + menu */}
                <div className="absolute top-3 right-3 flex items-center gap-1">
                  <Badge className={`text-[10px] font-medium border ${statusConfig.className}`}>
                    {state === "completed" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                    {statusConfig.label}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={(e) => handleEditClick(e, company)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Client
                      </DropdownMenuItem>
                      {company.assessment_id && (
                        <DropdownMenuItem onClick={(e) => handleHistoryClick(e, company)}>
                          <History className="h-4 w-4 mr-2" />
                          View History
                        </DropdownMenuItem>
                      )}
                      {state === "completed" && (
                        <DropdownMenuItem
                          onClick={(e) => handleRerunClick(e, company)}
                          disabled={rerunning}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Re-run Assessment
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => handleDeleteClick(e, company)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Client
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Client logo + name */}
                <div className="flex items-start gap-3 pr-28">
                  {company.logo_url ? (
                    <img
                      src={getLogoUrl(company.logo_url) || ""}
                      alt={`${company.name} logo`}
                      className="h-10 w-10 rounded object-contain border border-border bg-background shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded border border-border bg-muted flex items-center justify-center shrink-0">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base truncate">{company.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground truncate">
                        {company.questionnaire_id
                          ? getQuestionnaireDisplayTitle(company.questionnaire_id)
                          : "Self-Assessment"}
                      </span>
                      {company.version_number && company.version_number > 1 && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">
                          v{company.version_number}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Score — only when completed */}
                {state === "completed" && displayScore != null ? (
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${ratingColor}`}>{displayScore}</span>
                    <span className="text-sm text-muted-foreground">/ 100</span>
                    {!isHrv && (
                      <span className={`text-sm font-medium ml-auto ${ratingColor}`}>
                        {company.rating}
                      </span>
                    )}
                  </div>
                ) : state === "no_assessment" ? (
                  <p className="text-sm text-muted-foreground">No assessment started yet</p>
                ) : null}

                {/* Progress bar — only when in progress */}
                {state === "in_progress" && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Progress</span>
                      <span>{company.completion_pct}%</span>
                    </div>
                    <Progress value={company.completion_pct} className="h-2" />
                  </div>
                )}

                {/* Primary action button */}
                <Button
                  size="sm"
                  variant={state === "completed" ? "outline" : "default"}
                  className="w-full"
                  onClick={(e) => handlePrimaryAction(e, company)}
                >
                  {state === "completed" ? (
                    <>
                      <FileText className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      View Report
                    </>
                  ) : state === "in_progress" ? (
                    <>
                      <ArrowRight className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      Continue Assessment
                    </>
                  ) : (
                    <>
                      <Play className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      Start Assessment
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dual-mode Assessment Choice Dialog */}
      <Dialog open={showAssessmentMode} onOpenChange={setShowAssessmentMode}>
        <DialogContent className="sm:max-w-[500px] w-full overflow-hidden">
          <DialogHeader>
            <DialogTitle>How would you like to run this assessment?</DialogTitle>
            <DialogDescription>
              Choose whether to complete the assessment yourself or invite your client to fill it out.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button
              variant="outline"
              className="h-auto w-full py-4 flex flex-col items-start gap-1 text-left"
              onClick={handleCompleteMyselfClick}
            >
              <div className="flex items-center gap-2">
                <ClipboardEdit className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">Complete Assessment Myself</span>
              </div>
              <span className="text-xs text-muted-foreground ml-7">
                Fill out the questionnaire on behalf of your client
              </span>
            </Button>
            <Button
              variant="outline"
              className="h-auto w-full py-4 flex flex-col items-start gap-1 text-left"
              onClick={handleInviteClientClick}
            >
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary shrink-0" />
                <span className="font-semibold">Invite Client to Complete</span>
              </div>
              <span className="text-xs text-muted-foreground ml-7">
                Create a client user who can log in and complete the assessment
              </span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Client Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-[500px] w-full overflow-hidden">
          <DialogHeader>
            <DialogTitle>Invite Client Respondent</DialogTitle>
            <DialogDescription>
              Create a user account for your client. They will be assigned as a respondent to {selectedCompany?.name}'s assessment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email *</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="client@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteDisplayName">Display Name</Label>
              <Input
                id="inviteDisplayName"
                value={inviteDisplayName}
                onChange={(e) => setInviteDisplayName(e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invitePassword">Temporary Password *</Label>
              <Input
                id="invitePassword"
                type="password"
                value={invitePassword}
                onChange={(e) => setInvitePassword(e.target.value)}
                placeholder="Set a temporary password"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowInviteModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleInviteSubmit} disabled={inviting || !inviteEmail.trim() || !invitePassword.trim()} className="w-full sm:w-auto">
              {inviting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create & Invite"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Client
            </DialogTitle>
            <DialogDescription>
              Deleting <strong>{selectedCompany?.name}</strong> will remove all associated assessments and data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Permanently"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>
              Update the client name or logo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editName">Client Name *</Label>
              <Input
                id="editName"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Client name"
              />
            </div>
            <div className="space-y-2">
              <Label>Logo</Label>
              <LogoUpload onUploaded={() => {}} onFileSelected={setEditLogoFile} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={saving || !editName.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-run Assessment Confirmation Modal */}
      <Dialog open={showRerunConfirm} onOpenChange={setShowRerunConfirm}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Re-run Assessment
            </DialogTitle>
            <DialogDescription>
              This will create a <strong>new version</strong> of the assessment for{" "}
              <strong>{rerunCompany?.name}</strong>. The previous version and its results will be
              preserved as read-only history and can be viewed at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowRerunConfirm(false)}
              disabled={rerunning}
            >
              Cancel
            </Button>
            <Button onClick={handleRerunConfirm} disabled={rerunning}>
              {rerunning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating new version…
                </>
              ) : (
                "Create New Version"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assessment Version History Modal */}
      <AssessmentVersionHistoryModal
        open={showHistoryModal}
        onOpenChange={setShowHistoryModal}
        portfolioId={historyCompany?.portfolio_id ?? null}
        questionnaireId={historyCompany?.questionnaire_id ?? null}
        clientName={historyCompany?.name}
      />
    </>
  );
}
